"use client";

import { c as _c } from "react/compiler-runtime";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import NFTMarketplaceLinks from "@/components/nft-marketplace-links/NFTMarketplaceLinks";
import type { MemesExtendedData, NFT } from "@/entities/INFT";
import { numberWithCommas } from "@/helpers/Helpers";
import { buildTooltipId, TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { getFileMimeTypeFromMetadata } from "@/helpers/nft.helpers";
import { useIdentity } from "@/hooks/useIdentity";
import useCapacitor from "@/hooks/useCapacitor";
import { FireIcon } from "@heroicons/react/24/outline";
import { ArrowUpRightIcon, InformationCircleIcon as InformationCircleSolidIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import type { ReactNode } from "react";
import { Tooltip } from "react-tooltip";
const UNIQUE_PERCENT_TOOLTIP = "Unique % represents collectors diversity. Higher percentage means more different collectors.";
const SECTION_HEADER_TITLE_CLASS = "tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-leading-4 tw-text-iron-400";
const TOP_LABEL_CLASS = "tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-leading-4 tw-text-iron-500";
const CREATOR_NAME_CLASS = "tw-text-sm tw-font-semibold tw-leading-none tw-text-white tw-no-underline md:tw-text-lg";
const INLINE_METRIC_LABEL_CLASS = "tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-400";
const MARKET_METRIC_LABEL_BASE_CLASS = "tw-mb-1 md:tw-mb-2 tw-text-sm tw-font-medium tw-leading-5";
const MARKET_METRIC_VALUE_CLASS = "tw-text-sm tw-font-semibold tw-leading-5 tw-text-white md:tw-text-lg md:tw-leading-6";
const COLLECTOR_METRIC_VALUE_CLASS = "tw-text-sm tw-font-semibold tw-leading-5 tw-text-white md:tw-text-lg md:tw-leading-6";
const INLINE_STATS_ROW_CLASS = "tw-flex tw-flex-wrap tw-gap-x-10 tw-gap-y-6 sm:tw-gap-x-14";
const EDITION_STATS_ROW_CLASS = "tw-flex tw-flex-wrap tw-items-start tw-gap-x-6 tw-gap-y-6 md:tw-gap-x-10";
const MARKET_OVERVIEW_ROW_CLASS = "tw-flex tw-flex-wrap tw-items-start tw-gap-x-6 tw-gap-y-6 md:tw-gap-x-10";
const MEME_MINT_DATE_LOCALE = "en-US";
const MEME_MINT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
};
export function MemeCollectorsStats(t0) {
  const $ = _c(36);
  const {
    nftMeta
  } = t0;
  const percentUniqueExMuseumLabel = nftMeta.burnt > 0 ? "% Unique ex. burnt and 6529 museum" : "% Unique ex. 6529 museum";
  let t1;
  if ($[0] !== nftMeta.hodlers) {
    t1 = numberWithCommas(nftMeta.hodlers);
    $[0] = nftMeta.hodlers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nftMeta.collection_size || $[3] !== nftMeta.hodlers_rank || $[4] !== t1) {
    t2 = <InlineStatsMetric label="Collectors" value={t1} rank={nftMeta.hodlers_rank} total={nftMeta.collection_size} />;
    $[2] = nftMeta.collection_size;
    $[3] = nftMeta.hodlers_rank;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== nftMeta.museum_holdings) {
    t3 = numberWithCommas(nftMeta.museum_holdings);
    $[6] = nftMeta.museum_holdings;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== nftMeta.collection_size || $[9] !== nftMeta.museum_holdings_rank || $[10] !== t3) {
    t4 = <InlineStatsMetric label="6529 museum" value={t3} rank={nftMeta.museum_holdings_rank} total={nftMeta.collection_size} />;
    $[8] = nftMeta.collection_size;
    $[9] = nftMeta.museum_holdings_rank;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== nftMeta.percent_unique) {
    t5 = formatPercent(nftMeta.percent_unique);
    $[12] = nftMeta.percent_unique;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== nftMeta.collection_size || $[15] !== nftMeta.percent_unique_rank || $[16] !== t5) {
    t6 = <InlineStatsMetric label="% Unique" value={t5} rank={nftMeta.percent_unique_rank} total={nftMeta.collection_size} infoTitle={UNIQUE_PERCENT_TOOLTIP} />;
    $[14] = nftMeta.collection_size;
    $[15] = nftMeta.percent_unique_rank;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== nftMeta.burnt || $[19] !== nftMeta.collection_size || $[20] !== nftMeta.percent_unique_not_burnt || $[21] !== nftMeta.percent_unique_not_burnt_rank) {
    t7 = nftMeta.burnt > 0 && <InlineStatsMetric label="% Unique ex. burnt" value={formatPercent(nftMeta.percent_unique_not_burnt)} rank={nftMeta.percent_unique_not_burnt_rank} total={nftMeta.collection_size} />;
    $[18] = nftMeta.burnt;
    $[19] = nftMeta.collection_size;
    $[20] = nftMeta.percent_unique_not_burnt;
    $[21] = nftMeta.percent_unique_not_burnt_rank;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== nftMeta.percent_unique_cleaned) {
    t8 = formatPercent(nftMeta.percent_unique_cleaned);
    $[23] = nftMeta.percent_unique_cleaned;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== nftMeta.collection_size || $[26] !== nftMeta.percent_unique_cleaned_rank || $[27] !== percentUniqueExMuseumLabel || $[28] !== t8) {
    t9 = <InlineStatsMetric label={percentUniqueExMuseumLabel} value={t8} rank={nftMeta.percent_unique_cleaned_rank} total={nftMeta.collection_size} />;
    $[25] = nftMeta.collection_size;
    $[26] = nftMeta.percent_unique_cleaned_rank;
    $[27] = percentUniqueExMuseumLabel;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t2 || $[31] !== t4 || $[32] !== t6 || $[33] !== t7 || $[34] !== t9) {
    t10 = <section><div className={INLINE_STATS_ROW_CLASS}>{t2}{t4}{t6}{t7}{t9}</div></section>;
    $[30] = t2;
    $[31] = t4;
    $[32] = t6;
    $[33] = t7;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  return t10;
}
export function MemeEditionSizeStats(t0) {
  const $ = _c(29);
  const {
    nftMeta
  } = t0;
  const editionSizeExMuseumLabel = nftMeta.burnt > 0 ? "ex. burnt and 6529 museum" : "ex. 6529 museum";
  let t1;
  if ($[0] !== nftMeta.edition_size) {
    t1 = numberWithCommas(nftMeta.edition_size);
    $[0] = nftMeta.edition_size;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nftMeta.collection_size || $[3] !== nftMeta.edition_size_rank || $[4] !== t1) {
    t2 = <InlineStatsMetric label="Edition size" value={t1} rank={nftMeta.edition_size_rank} total={nftMeta.collection_size} />;
    $[2] = nftMeta.collection_size;
    $[3] = nftMeta.edition_size_rank;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== nftMeta.burnt || $[7] !== nftMeta.collection_size || $[8] !== nftMeta.edition_size_not_burnt || $[9] !== nftMeta.edition_size_not_burnt_rank) {
    t3 = nftMeta.burnt > 0 && <><InlineStatsMetric label="burnt" value={numberWithCommas(nftMeta.burnt)} icon={<FireIcon className="tw-h-4 tw-w-4 tw-text-red" />} /><InlineStatsMetric label="ex. burnt" value={numberWithCommas(nftMeta.edition_size_not_burnt)} rank={nftMeta.edition_size_not_burnt_rank} total={nftMeta.collection_size} /></>;
    $[6] = nftMeta.burnt;
    $[7] = nftMeta.collection_size;
    $[8] = nftMeta.edition_size_not_burnt;
    $[9] = nftMeta.edition_size_not_burnt_rank;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== nftMeta.edition_size_cleaned) {
    t4 = numberWithCommas(nftMeta.edition_size_cleaned);
    $[11] = nftMeta.edition_size_cleaned;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== editionSizeExMuseumLabel || $[14] !== nftMeta.collection_size || $[15] !== nftMeta.edition_size_cleaned_rank || $[16] !== t4) {
    t5 = <InlineStatsMetric label={editionSizeExMuseumLabel} value={t4} rank={nftMeta.edition_size_cleaned_rank} total={nftMeta.collection_size} />;
    $[13] = editionSizeExMuseumLabel;
    $[14] = nftMeta.collection_size;
    $[15] = nftMeta.edition_size_cleaned_rank;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== nftMeta.hodlers) {
    t6 = numberWithCommas(nftMeta.hodlers);
    $[18] = nftMeta.hodlers;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== nftMeta.collection_size || $[21] !== nftMeta.hodlers_rank || $[22] !== t6) {
    t7 = <InlineStatsMetric label="Collectors" value={t6} rank={nftMeta.hodlers_rank} total={nftMeta.collection_size} />;
    $[20] = nftMeta.collection_size;
    $[21] = nftMeta.hodlers_rank;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t5 || $[27] !== t7) {
    t8 = <section className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-py-6 md:tw-py-8"><div className={EDITION_STATS_ROW_CLASS}>{t2}{t3}{t5}{t7}</div></section>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}
function InlineStatsMetric(t0) {
  const $ = _c(23);
  const {
    label,
    value,
    rank,
    total,
    icon,
    infoTitle
  } = t0;
  let t1;
  if ($[0] !== infoTitle || $[1] !== label) {
    t1 = infoTitle ? buildTooltipId("meme-collector-stat", label, infoTitle) : undefined;
    $[0] = infoTitle;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tooltipId = t1;
  let t2;
  if ($[3] !== label) {
    t2 = <span className={INLINE_METRIC_LABEL_CLASS}>{label}</span>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== infoTitle || $[6] !== tooltipId) {
    t3 = tooltipId && infoTitle && <><span aria-label={infoTitle} className="tw-inline-flex tw-cursor-help tw-items-center" data-tooltip-id={tooltipId} role="img"><InformationCircleSolidIcon className="tw-h-3.5 tw-w-3.5 tw-text-iron-400" /></span><Tooltip id={tooltipId} content={infoTitle} place="top" style={TOOLTIP_STYLES} /></>;
    $[5] = infoTitle;
    $[6] = tooltipId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== icon || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2 md:tw-mb-2">{icon}{t2}{t3}</div>;
    $[8] = icon;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== value) {
    t5 = <span className={COLLECTOR_METRIC_VALUE_CLASS}>{value}</span>;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== rank || $[15] !== total) {
    t6 = rank !== undefined && total !== undefined && <CollectorRankBadge rank={rank} total={total} />;
    $[14] = rank;
    $[15] = total;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1.5">{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t7) {
    t8 = <div className="tw-min-w-[8.5rem]">{t4}{t7}</div>;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function CollectorRankBadge(t0) {
  const $ = _c(7);
  const {
    rank,
    total
  } = t0;
  let t1;
  if ($[0] !== rank) {
    t1 = numberWithCommas(rank);
    $[0] = rank;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== total) {
    t2 = numberWithCommas(total);
    $[2] = total;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-iron-800 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-leading-none tw-text-iron-400 md:tw-py-0.5 md:tw-text-[11px] md:tw-leading-4">Rank {t1}/{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function formatPercent(value: number) {
  return `${Math.round(value * 100 * 10) / 10}%`;
}
export function MemeArtworkDetails(t0) {
  const $ = _c(14);
  const {
    nft
  } = t0;
  let t1;
  if ($[0] !== nft.mint_date) {
    t1 = getMintDateParts(nft.mint_date);
    $[0] = nft.mint_date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mintDate = t1;
  let t2;
  if ($[2] !== nft.artist || $[3] !== nft.artist_seize_handle) {
    const artistHandles = getArtistHandles(nft.artist_seize_handle);
    const artistNames = getArtistNames(nft.artist);
    const creatorCount = Math.max(artistNames.length, artistHandles.length);
    t2 = creatorCount > 0 ? Array.from({
      length: creatorCount
    }, (_, index) => {
      const handle = artistHandles[index] ?? null;
      return {
        handle,
        display: artistNames[index] ?? handle ?? "not available"
      };
    }) : [{
      handle: null,
      display: "not available"
    }];
    $[2] = nft.artist;
    $[3] = nft.artist_seize_handle;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const creators = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={TOP_LABEL_CLASS}>Created by</div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== creators) {
    t4 = <div className="tw-min-w-0">{t3}<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-y-2">{creators.map((creator, index_0) => {
          const nextCreator = creators[index_0 + 1];
          const hasNextCreator = nextCreator !== undefined;
          const showComma = hasNextCreator && (creator.handle === null || nextCreator.handle === null);
          return <div key={`${creator.display}-${creator.handle ?? "plain"}-${index_0}`} className={`tw-flex tw-items-center ${hasNextCreator && !showComma ? "tw-mr-4" : ""}`}>{creator.handle !== null ? <CreatorProfileIdentity handle={creator.handle} display={creator.display} /> : <span className={CREATOR_NAME_CLASS}>{creator.display}</span>}{showComma && <span className="tw-whitespace-pre tw-text-sm tw-font-semibold tw-leading-none tw-text-iron-500 md:tw-text-lg">{" , "}</span>}</div>;
        })}</div></div>;
    $[6] = creators;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={TOP_LABEL_CLASS}>Mint date</div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== mintDate.date) {
    t6 = <div className="tw-min-w-fit">{t5}<div className="tw-flex tw-h-7 tw-flex-wrap tw-items-center sm:tw-justify-end"><span className="tw-text-sm tw-font-semibold tw-leading-none tw-text-white md:tw-text-lg">{mintDate.date}</span></div></div>;
    $[9] = mintDate.date;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <section className="tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-pb-6 tw-pt-8 md:tw-pb-8 lg:tw-pt-0"><div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-6 tw-gap-y-6">{t4}{t6}</div></section>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function CreatorProfileIdentity(t0) {
  const $ = _c(16);
  const {
    handle,
    display
  } = t0;
  let t1;
  if ($[0] !== handle) {
    t1 = {
      handleOrWallet: handle,
      initialProfile: null
    };
    $[0] = handle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    profile
  } = useIdentity(t1);
  const avatarLabel = profile?.handle ?? handle;
  let t2;
  if ($[2] !== avatarLabel) {
    t2 = getInitials(avatarLabel);
    $[2] = avatarLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-iron-400">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const avatarFallback = t3;
  const t4 = profile?.pfp;
  const t5 = `${avatarLabel} avatar`;
  let t6;
  if ($[6] !== avatarFallback || $[7] !== t4 || $[8] !== t5) {
    t6 = <ProfileAvatar pfpUrl={t4} size={ProfileBadgeSize.SMALL} alt={t5} fallbackContent={avatarFallback} />;
    $[6] = avatarFallback;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `/${handle}`;
  let t8;
  if ($[10] !== display || $[11] !== t7) {
    t8 = <Link href={t7} className={`${CREATOR_NAME_CLASS} hover:tw-text-iron-300`}>{display}</Link>;
    $[10] = display;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-gap-2.5">{t6}{t8}</div>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function getMintDateParts(date?: Date) {
  if (!date) {
    return {
      date: "-"
    };
  }
  const mintDate = new Date(date);
  if (Number.isNaN(mintDate.getTime())) {
    return {
      date: "-"
    };
  }
  return {
    date: mintDate.toLocaleString(MEME_MINT_DATE_LOCALE, MEME_MINT_DATE_FORMAT)
  };
}
function getArtistHandles(value: string | undefined) {
  return value?.split(",").map(handle => handle.trim()).filter(handle => handle.length > 0) ?? [];
}
function getArtistNames(value: string | undefined) {
  return value?.split(" / ").join(",").split(", ").join(",").split(" and ").join(",").split(",").map(name => name.trim()).filter(name => name.length > 0) ?? [];
}
function getInitials(value: string | undefined) {
  if (!value) {
    return "?";
  }
  return value.split(/[,\s]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();
}
function MemeDistributionPlanLink(t0) {
  const $ = _c(8);
  const {
    nft
  } = t0;
  let t1;
  if ($[0] !== nft) {
    t1 = getDistributionPlanLink(nft);
    $[0] = nft;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const distributionPlanLink = t1;
  const t2 = nft.has_distribution ? "_self" : "_blank";
  const t3 = nft.has_distribution ? undefined : "noopener noreferrer";
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span>Distribution Plan</span>;
    t5 = <ArrowUpRightIcon className="-tw-mr-1 tw-h-4 tw-w-4 tw-text-iron-500" />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== distributionPlanLink || $[5] !== t2 || $[6] !== t3) {
    t6 = <section className="tw-pt-6"><Link href={distributionPlanLink} target={t2} rel={t3} className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-300 tw-no-underline tw-transition-colors hover:tw-bg-iron-800 hover:tw-text-white">{t4}{t5}</Link></section>;
    $[4] = distributionPlanLink;
    $[5] = t2;
    $[6] = t3;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
export function MemeCardFileType(t0) {
  const $ = _c(4);
  const {
    nft
  } = t0;
  const metadata = nft.metadata as unknown;
  const t1 = typeof metadata === "string" || metadata !== null && typeof metadata === "object" ? metadata as Parameters<typeof getFileMimeTypeFromMetadata>[0] : undefined;
  let t2;
  if ($[0] !== t1) {
    t2 = getFileMimeTypeFromMetadata(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fileMimeType = t2;
  if (!fileMimeType) {
    return null;
  }
  let t3;
  if ($[2] !== fileMimeType) {
    t3 = <section className="tw-pb-4"><div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-iron-300"><MediaTypeBadge mimeType={fileMimeType} size="sm" showTooltip={false} showLabel={true} tone="color" labelClassName="tw-text-inherit tw-font-medium" /></div></section>;
    $[2] = fileMimeType;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function MemeMarketplaceLinks(t0) {
  const $ = _c(3);
  const {
    nft
  } = t0;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const showMarketplaceLinks = !capacitor.isIos || country === "US";
  if (!showMarketplaceLinks) {
    return null;
  }
  let t1;
  if ($[0] !== nft.contract || $[1] !== nft.id) {
    t1 = <div className="tw-flex tw-min-w-[8.5rem] tw-items-end"><NFTMarketplaceLinks contract={nft.contract} id={nft.id} /></div>;
    $[0] = nft.contract;
    $[1] = nft.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function MemeNftLivePanel(t0) {
  const $ = _c(25);
  const {
    nft
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className={`${SECTION_HEADER_TITLE_CLASS} tw-mb-4`}>Market Overview</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== nft.mint_price) {
    t2 = <MarketMetric label="Mint price" value={nft.mint_price} decimals={100000} unit="ETH" />;
    $[1] = nft.mint_price;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== nft.floor_price) {
    t3 = <MarketMetric label="Floor price" value={nft.floor_price} unit="ETH" />;
    $[3] = nft.floor_price;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== nft.market_cap) {
    t4 = <MarketMetric label="Market cap" value={nft.market_cap} decimals={100} unit="ETH" />;
    $[5] = nft.market_cap;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== nft.hodl_rate) {
    t5 = <MarketMetric label="TDH rate" value={nft.hodl_rate} decimals={100} />;
    $[7] = nft.hodl_rate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== nft.highest_offer) {
    t6 = <MarketMetric label="Highest offer" value={nft.highest_offer} unit="ETH" />;
    $[9] = nft.highest_offer;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== nft) {
    t7 = <MemeMarketplaceLinks nft={nft} />;
    $[11] = nft;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <div className={MARKET_OVERVIEW_ROW_CLASS}>{t2}{t3}{t4}{t5}{t6}{t7}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== nft) {
    t9 = <MemeDistributionPlanLink nft={nft} />;
    $[20] = nft;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <section className="tw-pt-6 md:tw-pt-8">{t1}{t8}{t9}</section>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function MarketMetric(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    decimals: t1,
    unit
  } = t0;
  const decimals = t1 === undefined ? 1000 : t1;
  let t2;
  if ($[0] !== decimals || $[1] !== value) {
    t2 = value > 0 ? numberWithCommas(Math.round(value * decimals) / decimals) : "N/A";
    $[0] = decimals;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formattedValue = t2;
  let t3;
  if ($[3] !== label) {
    t3 = <div className={`${MARKET_METRIC_LABEL_BASE_CLASS} tw-text-iron-400`}>{label}</div>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== formattedValue) {
    t4 = <span className={MARKET_METRIC_VALUE_CLASS}>{formattedValue}</span>;
    $[5] = formattedValue;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== formattedValue || $[8] !== unit) {
    t5 = unit && formattedValue !== "N/A" && <span className="tw-ml-1.5 tw-text-sm tw-font-medium tw-leading-none tw-text-iron-400">{unit}</span>;
    $[7] = formattedValue;
    $[8] = unit;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="tw-flex tw-items-baseline">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <div className="tw-min-w-[8.5rem]">{t3}{t6}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function getDistributionPlanLink(nft: NFT) {
  if (nft.has_distribution) {
    return `/the-memes/${nft.id}/distribution`;
  }
  if (nft.id > 3) {
    return `https://github.com/6529-Collections/thememecards/tree/main/card${nft.id}`;
  }
  return "https://github.com/6529-Collections/thememecards/tree/main/card1-3";
}